import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, BarChart3, Shield, Zap, Crown, TrendingUp, Users, Trophy, Star, ChevronRight } from 'lucide-react'
import { useLatestLotteryResult, useActiveAnnouncements, useAuth, useClientConfig } from '@workspace/public-utils'
import { Seo } from '@workspace/public-ui/components/Seo'

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

const getBallClass = (num: number, isBonus = false) => {
  if (isBonus) return 'star-ball star-ball-bonus'
  if (num <= 10) return 'star-ball star-ball-1-10'
  if (num <= 20) return 'star-ball star-ball-11-20'
  if (num <= 30) return 'star-ball star-ball-21-30'
  if (num <= 40) return 'star-ball star-ball-31-40'
  return 'star-ball star-ball-41-45'
}

const LotteryBall = ({ number, isBonus = false, delay = 0 }: { number: number; isBonus?: boolean; delay?: number }) => (
  <motion.div
    className={getBallClass(number, isBonus)}
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.4, delay: delay * 0.08, ease: 'easeOut' }}
  >
    {number}
  </motion.div>
)

const StarHomePage = () => {
  const config = useClientConfig()
  const { data: latestResult, isLoading: resultLoading } = useLatestLotteryResult()
  const { data: announcements } = useActiveAnnouncements()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen">
      <Seo
        title={`${config.brandName} - ${config.seoTitleSuffix}`}
        description="프리미엄 로또 번호 생성 및 분석 서비스 - 최신 당첨번호 조회와 통계 분석으로 더 스마트하게 로또를 즐기세요."
        canonicalPath="/"
      />

      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden bg-star-navy py-24 md:py-32 px-4">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Badge */}
            <motion.div variants={fadeUp} className="mb-6">
              <span className="star-badge bg-star-gold/20 text-star-gold-light">
                <Star className="w-3 h-3" />
                대한민국 프리미엄 복권 서비스
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-white tracking-tighter leading-[1.1] mb-6"
            >
              행운을 분석하다,
              <br />
              <span className="text-star-gold">확률을 설계하다.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              AI 확률 분석 시스템 <strong className="text-white font-semibold">STAR-GEN</strong>이
              데이터로 읽는 행운의 공식을 제안합니다.
              {' '}당첨되지 않은 이유를 분석할 때, 비로소 확률은 움직입니다.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row gap-3 justify-center items-center"
            >
              <Link to="/results" className="star-btn-gold">
                최신 당첨결과 보기
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/plans" className="star-btn-secondary !border-gray-700 !text-gray-300 hover:!bg-gray-800 hover:!text-white">
                구독 플랜 보기
                <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative gradient orbs */}
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-star-gold/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
      </section>

      {/* ── Latest Results Section ── */}
      {!resultLoading && latestResult && (
        <section className="star-section bg-white dark:bg-gray-900 transition-colors">
          <div className="star-container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeUp} className="text-center mb-10">
                <p className="text-sm font-semibold text-star-gold uppercase tracking-wider mb-2">Latest Draw</p>
                <h2 className="star-heading">
                  제 {latestResult.round_number}회 당첨번호
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  추첨일: {new Date(latestResult.draw_date).toLocaleDateString('ko-KR', {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })}
                </p>
              </motion.div>

              <motion.div variants={fadeUp} className="star-card max-w-3xl mx-auto">
                <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4 py-4">
                  {(latestResult.numbers || []).map((num: number, idx: number) => (
                    <LotteryBall key={idx} number={num} delay={idx} />
                  ))}
                  <span className="text-2xl text-gray-300 dark:text-gray-600 font-light mx-1">+</span>
                  <LotteryBall number={latestResult.bonus_number} isBonus delay={7} />
                </div>

                <div className="border-t border-gray-100 dark:border-gray-700 mt-4 pt-4 text-center">
                  <Link
                    to="/results"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-star-navy dark:text-star-gold hover:text-star-gold dark:hover:text-star-gold-light transition-colors"
                  >
                    전체 당첨결과 보기
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Features Section ── */}
      <section className="star-section bg-star-cream dark:bg-gray-950 transition-colors">
        <div className="star-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="text-center mb-14">
              <p className="text-sm font-semibold text-star-gold uppercase tracking-wider mb-2">Why Choose Us</p>
              <h2 className="star-heading">{config.whyChooseUs}</h2>
              <p className="star-subheading mt-3 max-w-2xl mx-auto">
                안전하고 투명한 복권 서비스로 더 스마트한 선택을 돕습니다
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: Shield,
                  title: '100% 안전 보장',
                  description: '정부 인증 복권 판매점으로 모든 거래가 안전하게 보호됩니다. 개인정보 보호와 보안에 최선을 다합니다.',
                  accent: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
                },
                {
                  icon: Zap,
                  title: '실시간 결과 확인',
                  description: '추첨 즉시 결과를 확인하고 당첨 여부를 바로 알 수 있습니다. 놓치는 결과 없이 빠르게 확인하세요.',
                  accent: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
                },
                {
                  icon: Crown,
                  title: 'VIP 구독 혜택',
                  description: '정기 구독 회원에게는 AI 분석 번호와 특별한 혜택을 제공합니다. 등급별 맞춤 서비스를 경험하세요.',
                  accent: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
                },
              ].map((feature, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeUp}
                  className="star-card hover:shadow-md transition-shadow duration-300"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${feature.accent}`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-star-navy dark:text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STAR-GEN Analysis Section ── */}
      <section className="star-section bg-white dark:bg-gray-900 transition-colors">
        <div className="star-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            {/* Text content */}
            <motion.div variants={fadeUp}>
              <span className="star-badge bg-star-navy/5 text-star-navy mb-4">
                <TrendingUp className="w-3 h-3" />
                AI Analysis System
              </span>
              <h2 className="star-heading mb-4">
                당첨되지 않은 이유를<br />분석할 때, 비로소<br />확률은 움직인다
              </h2>
              <p className="star-subheading mb-8">
                STAR-GEN은 수학적 분석, 이론적 추적, 조합 생성을 결합한 AI 확률 분석 시스템입니다. 
                과거 데이터에서 패턴을 찾고, 통계적으로 유의미한 번호 조합을 생성합니다.
              </p>
              <div className="space-y-4">
                {[
                  { label: '수학적 분석', desc: '빈도, 편차, 확률 분포 기반 번호 분석' },
                  { label: '이론적 추적', desc: '과거 패턴과 트렌드의 과학적 추적' },
                  { label: '조합 생성', desc: '분석 결과를 반영한 최적 번호 조합 도출' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-star-gold/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-star-gold">{idx + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-star-navy dark:text-white">{item.label}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/analysis" className="star-btn-primary">
                  번호 분석 보기
                  <BarChart3 className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            {/* Visual card */}
            <motion.div variants={fadeUp} className="relative">
              <div className="bg-gradient-to-br from-star-navy to-gray-800 rounded-3xl p-8 md:p-10 text-center">
                <p className="text-star-gold font-black text-5xl md:text-6xl tracking-tighter mb-3">STAR-GEN</p>
                <p className="text-gray-400 text-sm mb-8">AI Probability Analysis System</p>

                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: '수학적 분석', color: 'bg-emerald-500' },
                    { label: '이론적 추적', color: 'bg-amber-400' },
                    { label: '조합 생성', color: 'bg-orange-500' },
                  ].map((piece, idx) => (
                    <motion.div
                      key={idx}
                      className={`${piece.color} rounded-xl py-5 px-3 text-center`}
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.2 }}
                    >
                      <span className="text-white font-bold text-sm">{piece.label}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex justify-around text-center">
                    <div>
                      <p className="text-2xl font-bold text-white">1,100+</p>
                      <p className="text-xs text-gray-500">분석된 회차</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">98.5%</p>
                      <p className="text-xs text-gray-500">분석 정확도</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">24/7</p>
                      <p className="text-xs text-gray-500">실시간 분석</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative dot */}
              <div className="absolute -top-3 -right-3 w-6 h-6 bg-star-gold rounded-full hidden lg:block" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Statistics Section ── */}
      <section className="star-section bg-star-cream dark:bg-gray-950 transition-colors">
        <div className="star-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="text-center mb-12">
              <p className="text-sm font-semibold text-star-gold uppercase tracking-wider mb-2">Numbers We Trust</p>
              <h2 className="star-heading">믿을 수 있는 숫자들</h2>
              <p className="star-subheading mt-3">{config.statsSubtitle}</p>
            </motion.div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { icon: Users, label: '총 회원수', value: '50만+', sub: '믿고 이용하는 회원들' },
                { icon: Trophy, label: '총 당첨액', value: '1,250억원', sub: '누적 당첨금' },
                { icon: Star, label: '만족도', value: '98.5%', sub: '고객 만족도' },
                { icon: Crown, label: '1등 당첨자', value: '127명', sub: '꿈을 이룬 분들' },
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeUp}
                  className="star-card text-center"
                >
                  <div className="w-10 h-10 mx-auto rounded-xl bg-star-navy/5 dark:bg-star-gold/10 flex items-center justify-center mb-3">
                    <stat.icon className="w-5 h-5 text-star-navy dark:text-star-gold" />
                  </div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{stat.label}</p>
                  <p className="text-2xl md:text-3xl font-extrabold tracking-tighter text-star-navy dark:text-white mt-1">{stat.value}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stat.sub}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── How It Works Section ── */}
      <section className="star-section bg-white dark:bg-gray-900 transition-colors">
        <div className="star-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="text-center mb-14">
              <p className="text-sm font-semibold text-star-gold uppercase tracking-wider mb-2">How It Works</p>
              <h2 className="star-heading">간단한 3단계로 시작하세요</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  title: '로그인',
                  description: '간편하게 로그인하고 서비스를 시작하세요. 카카오, 구글 소셜 로그인을 지원합니다.',
                },
                {
                  step: '02',
                  title: '구독 선택',
                  description: '나에게 맞는 구독 플랜을 선택하세요. Bronze부터 Platinum까지 다양한 등급을 제공합니다.',
                },
                {
                  step: '03',
                  title: 'AI 분석 번호 수령',
                  description: '매주 AI가 분석한 맞춤 번호를 받아보세요. SMS와 마이페이지에서 확인할 수 있습니다.',
                },
              ].map((item, idx) => (
                <motion.div key={idx} variants={fadeUp} className="relative">
                  <div className="mb-4">
                    <span className="text-5xl font-extrabold tracking-tighter text-gray-100 dark:text-gray-800">{item.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-star-navy dark:text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{item.description}</p>
                  {idx < 2 && (
                    <div className="hidden md:block absolute top-8 -right-4 text-gray-200 dark:text-gray-700">
                      <ArrowRight className="w-6 h-6" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <section className="star-section bg-star-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeUp}
              className="text-3xl md:text-5xl font-extrabold text-white tracking-tighter leading-tight mb-4"
            >
              지금 바로 시작하세요
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-gray-400 mb-8 max-w-xl mx-auto">
              {config.smartSystemCta}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
              {isAuthenticated ? (
                <Link to="/dashboard" className="star-btn-gold">
                  마이페이지로 이동
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <Link to="/auth/login" className="star-btn-gold">
                  로그인하고 시작하기
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <Link to="/plans" className="star-btn-secondary !border-gray-700 !text-gray-300 hover:!bg-gray-800 hover:!text-white">
                구독 플랜 비교하기
              </Link>
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-star-gold/5 rounded-full blur-3xl" />
      </section>

      {/* ── Announcements Section ── */}
      {announcements && announcements.length > 0 && (
        <section className="star-section bg-star-cream dark:bg-gray-950 transition-colors">
          <div className="star-container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeUp} className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-star-navy dark:text-white">공지사항</h3>
              </motion.div>

              <div className="space-y-3">
                {announcements.slice(0, 3).map((announcement: any) => (
                  <motion.div
                    key={announcement.id}
                    variants={fadeUp}
                    className="star-card !p-5 flex items-center justify-between hover:shadow-sm transition-shadow cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-star-navy dark:text-white truncate">{announcement.title}</h4>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 line-clamp-1">{announcement.content}</p>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 ml-4">
                      {new Date(announcement.created_at).toLocaleDateString('ko-KR')}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  )
}

export default StarHomePage
